import { motion } from "framer-motion";
import { Shield, Sparkles, ArrowRight, Activity } from "lucide-react";
import { scrollTo } from "@/utils/constants";

const Hero = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden bg-mesh" data-testid="hero-section">
      <div className="orb orb-teal w-[500px] h-[500px] -top-40 -right-40" />
      <div className="orb orb-coral w-[400px] h-[400px] top-1/3 -left-60" />
      <div className="orb orb-purple w-[350px] h-[350px] bottom-0 right-1/4" />
      <div className="orb orb-yellow w-[300px] h-[300px] bottom-20 left-1/4" />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex gap-2 mb-6">
              <span className="badge-glass inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium text-primary" data-testid="badge-validated">
                <Shield className="w-3.5 h-3.5 text-[#4ECDC4]" /> Clinically Validated
              </span>
              <span className="badge-glass inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium text-primary" data-testid="badge-ai">
                <Sparkles className="w-3.5 h-3.5 text-[#FFE66D]" /> AI Health Screening
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-primary leading-[1.1] mb-6" data-testid="hero-heading">
              Know Your Child.
              <span className="text-[#4ECDC4]"> Completely.</span>
            </h1>

            <p className="text-secondary text-lg md:text-xl leading-relaxed mb-8 max-w-lg" data-testid="hero-subheading">
              Comprehensive 360° health screening for growth, nutrition, cognition, dental health, and child development — all in one visit.
            </p>

            <div className="flex flex-wrap gap-3">
              <button 
                onClick={() => scrollTo('contact')}
                className="group flex items-center gap-2 btn-coral px-7 py-3.5 rounded-2xl font-semibold transition-all"
                data-testid="hero-book-button"
              >
                Book a Screening 
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={() => scrollTo('services')}
                className="btn-glass px-7 py-3.5 rounded-2xl font-medium text-primary"
                data-testid="hero-learn-button"
              >
                Learn About tots360
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="glass-card p-3 rounded-3xl">
              <img 
                src="/hero-kids.png"
                alt="Happy children of different ages"
                className="w-full h-[400px] md:h-[480px] object-cover rounded-2xl"
                loading="lazy"
                data-testid="hero-image"
              />
            </div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="absolute -bottom-4 -left-4 glass-strong p-4 rounded-2xl"
            >
              <div className="flex items-center gap-3">
                <motion.div 
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="w-11 h-11 rounded-xl flex items-center justify-center shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #4ECDC4 0%, #2a9d8f 100%)' }}
                >
                  <Activity className="w-5 h-5 text-white" />
                </motion.div>
                <div>
                  <motion.div 
                    animate={{ width: ['40%', '85%', '60%', '95%', '70%'] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    className="h-1.5 bg-gradient-to-r from-[#4ECDC4] to-[#A855F7] rounded-full mb-1.5"
                    style={{ maxWidth: '80px' }}
                  />
                  <motion.div 
                    animate={{ width: ['70%', '45%', '90%', '55%', '80%'] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                    className="h-1.5 bg-gradient-to-r from-[#FF6B6B] to-[#FFE66D] rounded-full"
                    style={{ maxWidth: '60px' }}
                  />
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="absolute -top-2 -right-2 glass p-3 rounded-xl"
            >
              <div className="flex items-center gap-2">
                {['#FF6B6B', '#4ECDC4', '#A855F7'].map((color, i) => (
                  <motion.div 
                    key={i}
                    animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.3, ease: "easeInOut" }}
                    className="w-3 h-3 rounded-full"
                    style={{ background: color }}
                  />
                ))}
                <Sparkles className="w-4 h-4 text-[#FFE66D]" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
